import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useToggle from "hooks/useToggle";
import { SHOPIFY_DOMAIN } from "utils/constants";
import axios from "axios";
import Flatpickr from "react-flatpickr";
import { formatToSimpleDate } from "helpers/format-date";
import { useGlobalContext } from "context/global";
import { isDateAvailable } from "utils/subscription-date";
import { getLanguageCode } from "utils/language";

const ZapietCalendar = ({
	inline, minDaysOffset = 0, hideError = false, render, customOptions = {}, CustomLoader, value, ...props
}) => {
	const { subscription, profile } = useSelector(state => state.account);
	const { chargeOffsetDays, attributes } = subscription;
	const { latestSubOrder } = profile;
	const { language } = useGlobalContext();
	const locale = getLanguageCode(language);
	const [error, setError] = useState(false);
	const [isLoading, toggleLoading] = useToggle(true);
	const [zapietData, setZapietData] = useState(null);
	const [flatpickrLocale, setFlatpickrLocale] = useState("en");

	useEffect(() => {
		getLocale(locale).then(setFlatpickrLocale);
	}, [locale]);

	const deliveryLocationId = attributes?.find(attr => attr.key === "Delivery-Location-Id");

	const latestOrderBillingDate = new Date(latestSubOrder?.createdAt);
	const latestOrderDeliveryDate = latestOrderBillingDate.setDate(latestOrderBillingDate.getDate() + chargeOffsetDays);

	const maxDate = new Date();
	maxDate.setDate(maxDate.getDate() + 60);

	useEffect(() => {
		if (!deliveryLocationId || zapietData) return;
		if (error) setError(false);
		fetchCalendar(deliveryLocationId?.value).then((data) => {
			if (data.error)
				setError(true);
			else
				setZapietData(data);

			isLoading && toggleLoading();
		});
	}, []);

	if (error) {
		if (hideError) return null;
		return (
			<div className="mx-auto h-[250px] flex flex-col justify-center">
				<h4 className="text-red-500 text-center">Error fetching available dates. Please try again later.</h4>
			</div>
		);
	}

	if (isLoading) {
		if (CustomLoader) return <CustomLoader />;
		return (
			<div role="status" className="w-full animate-pulse">
				<div className="mx-auto bg-gray-100 w-[307px] h-[296px]" />
			</div>
		);
	}

	if (!zapietData && !isLoading) return null;

	return (
		<Flatpickr
			value={formatToSimpleDate(value)}
			options={{
				monthSelectorType: "static",
				locale: flatpickrLocale,
				minDate: new Date(new Date(zapietData?.minDate).getTime() + minDaysOffset * 24 * 60 * 60 * 1000),
				maxDate,
				inline,
				disable: [
					function (date) {
						if (!zapietData) {
							return false;
						}
						return !isDateAvailable(date, zapietData.disabled, latestOrderDeliveryDate);
					}
				],
				dateFormat: "Y-m-d",
				...customOptions,
			}}
			render={render || (({ value }, ref) => (
				<button className="hidden" ref={ref} value={value} />
			))}
			{...props}
		/>
	);
};

const getLocale = async (locale) => {
	switch (locale) {
		case "de":
			return (await import("flatpickr/dist/l10n/de.js")).German;
		case "nl":
			return (await import("flatpickr/dist/l10n/nl.js")).Dutch;
		default:
			return "en";
	}
};


const fetchCalendar = async (locationId) => {
	try {
		const response = await axios.get(`https://api.zapiet.com/v1.0/delivery/locations/${locationId}/calendar?shop=${SHOPIFY_DOMAIN}`);
		return response.data;
	} catch (err) {
		console.log("err fetching zapiet calendar", err);
		return { error: true };
	}
};

export default ZapietCalendar;
